import { parseBrandBackground } from '@vedalib/editor/lib/brand'
import { CONFIG_COURSE } from '@vedalib/editor/lib/elements'
import { useKeyPress } from 'ahooks'
import useEmbedParams from 'hooks/useEmbedParams'
import React, { useEffect, useRef } from 'react'

import { useGetBgImageStyles } from 'components/Course/useGetBgImageStyles'
import { AbsolutePortalProvider } from 'components/Editor/components/AbsolutePortal'
import TestProgressFooter from 'components/Editor/cource/controls/TestProgressFooter'
import BlockRenderContainer from 'components/Editor/cource/layout/Block/BlockContainer'
import Scrollbar from 'components/Editor/cource/renderEntry/CourseSection/Scrollbar'
import { useBrandTheme, useElementStyleCss } from 'services/Branding/hooks'
import ScrollService from 'services/Scroll/ScrollService'
import { ScrollContainerEnum } from 'services/Scroll/enums'
import { setBlockViewed } from 'services/Store/Project/actions'
import { BlockMode } from 'services/Store/Project/enums'
import { useProjectContext, useProjectDispatch } from 'services/Store/Project/hooks'
import { getBlock } from 'services/Store/Project/selectors'
import type { IBlockMode, Section } from 'services/Store/Project/types'
import { useBlockViewed } from 'services/lms/hooks'

import * as s from './CourseTestSection.module.scss'

interface ICourseTestSectionProps {
  mode: IBlockMode
  section: Section
}

const CourseTestSection: React.FC<ICourseTestSectionProps> = ({ mode, section }) => {
  const ref = useRef<HTMLDivElement>(null)
  const currentBlock = useProjectContext(getBlock)
  const firstBlock = useProjectContext(getBlock, { first: true })
  const theme = useBrandTheme()
  const dispatch = useProjectDispatch()

  const { hideFooter } = useEmbedParams()

  const block = currentBlock || firstBlock

  useKeyPress(['meta.leftarrow', 'meta.rightarrow', 'alt.leftarrow', 'alt.rightarrow'], (e) => {
    e.preventDefault()
  })

  const isVisibleProgress = !(block?.mode && [BlockMode.start, BlockMode.end].includes(block?.mode))
  const styles = useElementStyleCss(CONFIG_COURSE.testSectionLayout.type, mode.deviceMode)
  const sectionBackground = parseBrandBackground(section?.style?.background || theme?.background)
  const imgStyles = useGetBgImageStyles(sectionBackground.overlay)

  useBlockViewed({ blockId: block?.uuid || '', mode, ref })

  useEffect(() => {
    if (block?.uuid) {
      dispatch(setBlockViewed(block?.uuid))
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block?.uuid])

  return (
    <div className={s.root} style={{ ...imgStyles, ...styles.indents }}>
      <Scrollbar ref={ScrollService.createContainerSetter(ScrollContainerEnum.canvas)}>
        <AbsolutePortalProvider viewport={ref}>
          <main className={s.content} ref={ref} tabIndex={-1}>
            {block && <BlockRenderContainer block={block} index={0} mode={mode} />}
          </main>
        </AbsolutePortalProvider>
      </Scrollbar>
      {isVisibleProgress && !hideFooter && (
        <div className={s.footer}>
          <TestProgressFooter mode={mode} />
        </div>
      )}
    </div>
  )
}

export default CourseTestSection
